/**
 * ConversationalWorkoutCoach - Event-driven AI coach orchestrator for web
 *
 * Adapted from kinasticCoach useConversationalWorkoutCoach hook.
 * Uses plain class with MobX reactions instead of React hooks.
 */
import { reaction, makeAutoObservable, IReactionDisposer } from 'mobx';
import type { ViewWorkout } from '../../Model/WorkoutLog/ViewWorkout';
import type { ViewSet } from '../../Model/WorkoutLog/ViewSet';
import { OpenAIService, mapToOpenAIVoice } from './OpenAIService';
import { CoachEvent, CoachEventContext, WorkoutCoachSession } from './WorkoutCoachSession';

const LOG_PREFIX = '[ConvCoach]';
const BREAK_TRIGGER_SECONDS = 10;

const EVENT_PRIORITY: Record<CoachEvent, number> = {
  workout_complete: 100,
  set_complete: 80,
  exercise_intro: 70,
  workout_start: 60,
  set_starting: 50,
  value_changed: 40,
  milestone: 30,
  break_reminder: 20,
};

type AudioQueueItem = {
  event: CoachEvent;
  priority: number;
  viewSet: ViewSet;
  additionalContext?: Partial<CoachEventContext>;
  timestamp: number;
};

type RemoteSettings = {
  get(key: string): any;
};

export class ConversationalWorkoutCoach {
  isLoading: boolean = false;
  lastText: string | null = null;

  private viewWorkout: ViewWorkout;
  private language: string;
  private remoteSettings: RemoteSettings;

  private session: WorkoutCoachSession;
  private disposers: IReactionDisposer[] = [];
  private processedEvents = new Set<string>();
  private currentGenerationId: number = 0;
  private abortController: AbortController | null = null;
  private audioQueue: AudioQueueItem[] = [];
  private isProcessingQueue: boolean = false;
  private currentAudioElement: HTMLAudioElement | null = null;

  // Tracking previous values for change detection
  private prevPhase: string | null = null;
  private prevViewSetId: string | null = null;
  private prevValues: { reps?: number; weight?: number; duration?: number } = {};

  constructor(viewWorkout: ViewWorkout, language: string, remoteSettings: RemoteSettings) {
    this.viewWorkout = viewWorkout;
    this.language = language;
    this.remoteSettings = remoteSettings;
    this.session = new WorkoutCoachSession(viewWorkout, language);

    makeAutoObservable(this, {
      start: false,
      dispose: false,
    });
  }

  start(): void {
    console.info(`${LOG_PREFIX} Starting AI coach`);

    // React to phase changes (workout_start, set_complete, exercise_intro, workout_complete)
    this.disposers.push(
      reaction(
        () => this.viewWorkout.currentPhase,
        (phase, prevPhase) => {
          if (!this.isEnabled) return;
          const viewSet = this.viewWorkout.selectedViewSet;
          if (!viewSet) return;

          // Countdown -> welcome, or late start (coach initialized after workout already active)
          if (
            (phase === 'countdown' && prevPhase !== 'countdown') ||
            (phase === 'active' && prevPhase === undefined)
          ) {
            console.info(`${LOG_PREFIX} Triggering workout_start (phase: ${phase}, prevPhase: ${prevPhase})`);
            this.queueEvent('workout_start', viewSet);
          }

          // Active -> break = set complete
          if (prevPhase === 'active' && phase === 'break') {
            const isLastSetOfExercise = viewSet.index === viewSet.viewBlock.sets.length - 1;
            const nextSet = viewSet.nextSet;
            const isNewExerciseNext = nextSet && nextSet.exercise?.id !== viewSet.exercise?.id;

            if (isLastSetOfExercise && isNewExerciseNext) {
              console.info(`${LOG_PREFIX} Exercise complete, introducing next`);
              this.queueEvent('exercise_intro', viewSet);
            } else {
              console.info(`${LOG_PREFIX} Set complete`);
              this.queueEvent('set_complete', viewSet);
            }
          }

          // Workout finished
          if (phase === 'finished' && prevPhase !== 'finished') {
            console.info(`${LOG_PREFIX} Workout finished!`);
            this.queueEvent('workout_complete', viewSet);
          }

          this.prevPhase = phase;
        },
        { fireImmediately: true },
      ),
    );

    // React to viewSet changes (set_starting)
    this.disposers.push(
      reaction(
        () => this.viewWorkout.selectedViewSet?.id,
        (newId) => {
          if (!this.isEnabled) return;

          const prevId = this.prevViewSetId;
          const viewSet = this.viewWorkout.selectedViewSet;
          const phase = this.viewWorkout.currentPhase;

          if (prevId !== null && prevId !== newId) {
            // ViewSet actually changed - abort stale requests and stop audio
            console.info(`${LOG_PREFIX} ViewSet changed from ${prevId} to ${newId}`);
            this.abortCurrentGeneration();
            this.stopCurrentAudio();
          }

          // Trigger set_starting: both for set changes AND late init (prevId === null, already active)
          if (prevId !== newId && viewSet && phase === 'active') {
            console.info(`${LOG_PREFIX} Exercise started (viewSet: ${prevId === null ? 'late-init' : 'changed'})`);
            this.queueEvent('set_starting', viewSet);
          }

          this.prevViewSetId = newId ?? null;
        },
        { fireImmediately: true },
      ),
    );

    // React to value changes
    this.disposers.push(
      reaction(
        () => {
          const vs = this.viewWorkout.selectedViewSet;
          if (!vs?.logSet) return null;
          return {
            reps: vs.logSet.values.get('REPETITIONS'),
            weight: vs.logSet.values.get('WEIGHT'),
            duration: vs.logSet.values.get('DURATION'),
          };
        },
        (current) => {
          if (!this.isEnabled || !current) return;
          const viewSet = this.viewWorkout.selectedViewSet;
          if (!viewSet || this.viewWorkout.currentPhase !== 'active') return;

          const prev = this.prevValues;
          const repsChanged = prev.reps !== undefined && current.reps !== prev.reps;
          const weightChanged = prev.weight !== undefined && current.weight !== prev.weight;
          const durationChanged = prev.duration !== undefined && current.duration !== prev.duration;

          if (repsChanged || weightChanged || durationChanged) {
            console.info(`${LOG_PREFIX} Value changed`);
            this.processedEvents.delete(`${viewSet.id}-value_changed`);
            this.queueEvent('value_changed', viewSet, {
              previousReps: prev.reps,
              previousWeight: prev.weight,
              previousDuration: prev.duration,
            });
          }

          this.prevValues = current;
        },
        { fireImmediately: true },
      ),
    );

    // React to break timer for break_reminder
    this.disposers.push(
      reaction(
        () => {
          const vs = this.viewWorkout.selectedViewSet;
          if (!vs) return null;
          return { remainingBreak: vs.remainingBreak, id: vs.id };
        },
        (data) => {
          if (!this.isEnabled || !data) return;
          if (this.viewWorkout.currentPhase !== 'break') return;

          const viewSet = this.viewWorkout.selectedViewSet;
          if (!viewSet) return;

          const remainingSeconds = data.remainingBreak / 1000;
          const eventKey = `${data.id}-break_reminder`;

          if (
            remainingSeconds > 0 &&
            remainingSeconds <= BREAK_TRIGGER_SECONDS &&
            remainingSeconds > BREAK_TRIGGER_SECONDS - 1 &&
            !this.processedEvents.has(eventKey)
          ) {
            console.info(`${LOG_PREFIX} Break ending reminder at ${remainingSeconds.toFixed(1)}s`);
            this.queueEvent('break_reminder', viewSet, { remainingBreakSeconds: remainingSeconds });
          }
        },
      ),
    );
  }

  dispose(): void {
    console.info(`${LOG_PREFIX} Disposing AI coach`);
    this.disposers.forEach((d) => d());
    this.disposers = [];
    this.abortCurrentGeneration();
    this.stopCurrentAudio();
    this.session.abort();
    this.audioQueue = [];
    this.isProcessingQueue = false;
  }

  private get isEnabled(): boolean {
    return (
      this.remoteSettings.get('aiCoachEnabled') !== false &&
      this.remoteSettings.get('audioFeedback') !== false
    );
  }

  private abortCurrentGeneration(): void {
    this.abortController?.abort();
    this.abortController = null;
    this.session.abort();
    this.currentGenerationId++;
    console.info(`${LOG_PREFIX} Aborted, new generation: ${this.currentGenerationId}`);
  }

  private stopCurrentAudio(): void {
    if (this.currentAudioElement) {
      try {
        this.currentAudioElement.pause();
        this.currentAudioElement.src = '';
      } catch {
        // Already stopped
      }
      this.currentAudioElement = null;
    }
  }

  private async queueEvent(
    event: CoachEvent,
    eventViewSet: ViewSet,
    additionalContext?: Partial<CoachEventContext>,
  ): Promise<void> {
    const eventKey = `${eventViewSet.id}-${event}`;

    if (this.processedEvents.has(eventKey)) {
      return;
    }

    const priority = EVENT_PRIORITY[event] || 0;

    // If higher priority than current, abort and process immediately
    const currentPriority = this.audioQueue[0]?.priority || 0;
    if (priority > currentPriority && this.isProcessingQueue) {
      console.info(`${LOG_PREFIX} High priority event, preempting`, { event, priority });
      this.abortCurrentGeneration();
      this.stopCurrentAudio();
      this.audioQueue = [];
      this.isProcessingQueue = false;
    }

    this.processedEvents.add(eventKey);

    this.audioQueue.push({
      event,
      priority,
      viewSet: eventViewSet,
      additionalContext,
      timestamp: Date.now(),
    });

    this.audioQueue.sort((a, b) => b.priority - a.priority);

    if (!this.isProcessingQueue) {
      this.isProcessingQueue = true;

      while (this.audioQueue.length > 0) {
        const item = this.audioQueue.shift()!;
        console.info(`${LOG_PREFIX} Processing from queue`, { event: item.event });
        await this.processEvent(item.event, item.viewSet, item.additionalContext);
      }

      this.isProcessingQueue = false;
    }
  }

  private async processEvent(
    event: CoachEvent,
    eventViewSet: ViewSet,
    additionalContext?: Partial<CoachEventContext>,
  ): Promise<void> {
    try {
      if (!this.isEnabled) return;

      const myGeneration = this.currentGenerationId;
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.isLoading = true;

      const overallStart = Date.now();
      const ctx: CoachEventContext = {
        event,
        viewSet: eventViewSet,
        language: this.language,
        ...additionalContext,
      };

      // Step 1: Generate text
      const textStart = Date.now();
      const result = await this.session.generateResponse(ctx, async (messages, _options, sig) => {
        return OpenAIService.chat(messages, {}, sig || signal);
      });

      if (!result || myGeneration !== this.currentGenerationId) {
        console.warn(`${LOG_PREFIX} Discarding stale result`);
        return;
      }

      const textDuration = Date.now() - textStart;
      console.info(`${LOG_PREFIX} Text generated in ${textDuration}ms`, {
        ssmlLength: result.ssml.length,
        textPreview: result.text.substring(0, 80),
      });

      this.lastText = result.text;

      // Step 2: Convert SSML to speech (Google TTS via backend)
      const voiceIdentifier =
        (this.remoteSettings.get('voiceIdentifier') as string | undefined) ?? 'male_01';
      const ttsStart = Date.now();
      const audioBuffer = await OpenAIService.speakSSMLBackend(
        result.ssml, voiceIdentifier, signal, this.language,
      );

      if (myGeneration !== this.currentGenerationId) {
        console.warn(`${LOG_PREFIX} Discarding stale audio`);
        return;
      }

      const ttsDuration = Date.now() - ttsStart;
      console.info(`${LOG_PREFIX} TTS completed in ${ttsDuration}ms`);

      // Step 3: Play audio
      await this.playAudioBuffer(audioBuffer);

      const totalDuration = Date.now() - overallStart;
      console.info(`${LOG_PREFIX} Ready in ${totalDuration}ms`, {
        text: `${textDuration}ms`,
        tts: `${ttsDuration}ms`,
        sessionSummary: this.session.getSessionSummary(),
      });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        console.info(`${LOG_PREFIX} Request aborted`);
      } else {
        console.error(`${LOG_PREFIX} Failed:`, error);
      }
    } finally {
      this.isLoading = false;
    }
  }

  private async playAudioBuffer(buffer: ArrayBuffer): Promise<void> {
    console.info(`${LOG_PREFIX} playAudioBuffer()`, { bufferSize: buffer.byteLength });

    // Use Blob URL + HTML Audio element — more reliable than AudioContext on web
    // (avoids browser autoplay policy re-suspension issues)
    const blob = new Blob([buffer], { type: 'audio/mp3' });
    const url = URL.createObjectURL(blob);

    try {
      const audio = new Audio(url);
      this.currentAudioElement = audio;

      console.info(`${LOG_PREFIX} Audio playback starting`);

      await new Promise<void>((resolve, reject) => {
        audio.onended = () => {
          console.info(`${LOG_PREFIX} Audio playback ended`);
          if (this.currentAudioElement === audio) {
            this.currentAudioElement = null;
          }
          resolve();
        };
        audio.onerror = (e) => {
          console.error(`${LOG_PREFIX} Audio playback error`, e);
          reject(new Error('Audio playback failed'));
        };
        audio.play().catch(reject);
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}
